/**
 * Customer route view.renderers.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import {
  CustomerAccessRequiredPage,
  CustomerActivateAccountPage,
  CustomerAuthPage,
  CustomerForgotPasswordPage,
  CustomerInitializingPage,
  CustomerResetPasswordPage,
} from './components'
import type { useCustomerRouteViewModel } from './hooks'
import type { CustomerRouteMode } from './routes'
import { renderCustomerProtectedContent } from './customerRouteView.protectedRenderers'

type CustomerRouteViewModel = ReturnType<typeof useCustomerRouteViewModel>

/**
 * Render customer auth route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const renderCustomerAuthRoute = ({
  mode,
  onNavigate,
  viewModel,
}: {
  mode: CustomerRouteMode
  onNavigate: (targetPath: string) => void
  viewModel: CustomerRouteViewModel
}) =>
  mode === 'activate' ? (
    <CustomerActivateAccountPage />
  ) : mode === 'forgot-password' ? (
    <CustomerForgotPasswordPage />
  ) : mode === 'reset-password' ? (
    <CustomerResetPasswordPage />
  ) : mode === 'login' || mode === 'signup' ? (
    <CustomerAuthPage
      mode={mode}
      error={viewModel.error}
      busy={viewModel.busy}
      initializing={viewModel.initializing}
      nextPath={viewModel.nextPath}
      nextPathReady={viewModel.nextPathReady}
      onClearError={viewModel.clearError}
      onNavigate={onNavigate}
      onLogin={viewModel.login}
      onVerifyLoginSecondFactor={viewModel.verifyLoginSecondFactor}
      onVerifyLoginSecondFactorWithPasskey={viewModel.verifyLoginSecondFactorWithPasskey}
      onSignup={viewModel.signup}
    />
  ) : null

/**
 * Render customer protected route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const renderCustomerProtectedRoute = ({
  onNavigate,
  viewModel,
}: {
  onNavigate: (targetPath: string) => void
  viewModel: CustomerRouteViewModel
}) => {
  if (viewModel.initializing) {
    return <CustomerInitializingPage />
  }

  if (!viewModel.authenticated || !viewModel.account || !viewModel.profile) {
    return <CustomerAccessRequiredPage requestedAccountPath={viewModel.requestedAccountPath} />
  }

  return renderCustomerProtectedContent({
    onNavigate,
    viewModel,
  })
}
